import { FileText, AudioLines, Activity } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { ControlPanel } from './ControlPanel';
import { LogStream } from './LogStream';
import type { Status, LogEntry } from '@/lib/api';

interface DashboardProps {
  status: Status;
  logs: LogEntry[];
  onStartProducer: () => void;
  onStartAudio: () => void;
  onStopAll: () => void;
  isLoading: boolean;
}

export function Dashboard({
  status,
  logs,
  onStartProducer,
  onStartAudio,
  onStopAll,
  isLoading
}: DashboardProps) {
  const overallProgress = Math.round(
    ((status.producer.completed + status.audio.completed) / (status.producer.total * 2)) * 100
  );

  return (
    <div className="min-h-screen bg-gray-950 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* 头部 */}
        <header className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-100">Naval Factory</h1>
          <p className="text-gray-400">AI 驱动的播客内容生产系统</p>
        </header>

        {/* 整体进度 */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-6 h-6 text-primary-500" />
              <h2 className="text-xl font-semibold text-gray-200">整体进度</h2>
            </div>
            <span className="text-3xl font-bold text-primary-500">{overallProgress}%</span>
          </div>
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary-600 to-primary-400 transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500 text-center">
            {status.producer.completed + status.audio.completed} / {status.producer.total * 2} 任务
          </p>
        </div>

        {/* 控制面板 */}
        <ControlPanel
          producerStatus={status.producer}
          audioStatus={status.audio}
          onStartProducer={onStartProducer}
          onStartAudio={onStartAudio}
          onStopAll={onStopAll}
          isLoading={isLoading}
        />

        {/* 任务状态 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <ProgressBar
              status={status.producer}
              label="脚本生成"
              icon={<FileText className="w-5 h-5 text-primary-500" />}
            />
          </div>

          <div className="card">
            <ProgressBar
              status={status.audio}
              label="音频生成"
              icon={<AudioLines className="w-5 h-5 text-primary-500" />}
            />
          </div>
        </div>

        {/* 日志流 */}
        <LogStream logs={logs} />
      </div>
    </div>
  );
}
